import { create } from 'zustand';
import type { PreviewMode, ResolvedElement, ADACheckResult } from '../types/preview';
import { resolveElements, runADAComplianceChecks } from '../lib/previewResolver';
import { useStudioStore } from './studioStore';
import { useMappingStore } from './mappingStore';

interface PreviewStoreState {
  isPreviewActive: boolean;
  previewMode: PreviewMode;
  resolvedElements: ResolvedElement[];
  isResolving: boolean;
  previewZoom: number;
  showBoundaries: boolean;
  showLayerIndicators: boolean;
  adaCheckResults: ADACheckResult[];
  showADAPanel: boolean;

  enterPreview: () => void;
  exitPreview: () => void;
  setPreviewMode: (mode: PreviewMode) => void;
  resolveAllElements: () => void;
  setPreviewZoom: (zoom: number) => void;
  toggleBoundaries: () => void;
  toggleLayerIndicators: () => void;
  runADAChecks: () => void;
  toggleADAPanel: () => void;
  setResolvedElements: (elements: ResolvedElement[]) => void;
}

export const usePreviewStore = create<PreviewStoreState>((set, get) => ({
  isPreviewActive: false,
  previewMode: 'combined',
  resolvedElements: [],
  isResolving: false,
  previewZoom: 1,
  showBoundaries: false,
  showLayerIndicators: false,
  adaCheckResults: [],
  showADAPanel: false,

  enterPreview: () => {
    set({ isPreviewActive: true, previewMode: 'combined' });
    get().resolveAllElements();
    get().runADAChecks();
  },

  exitPreview: () => {
    set({
      isPreviewActive: false,
      resolvedElements: [],
      adaCheckResults: [],
      showADAPanel: false,
      showBoundaries: false,
      showLayerIndicators: false,
    });
  },

  setPreviewMode: (mode: PreviewMode) => {
    set({ previewMode: mode });
  },

  resolveAllElements: () => {
    set({ isResolving: true });

    try {
      const { canvasState } = useStudioStore.getState();
      const { elementMappings, preview } = useMappingStore.getState();

      const previewRecords = preview?.records ?? null;
      const previewRecordIndex = preview?.selectedRecordIndex ?? 0;
      const previewTableName = preview?.tableName ?? null;

      const resolved = resolveElements(
        canvasState.elements,
        elementMappings,
        previewRecords,
        previewRecordIndex,
        previewTableName
      );

      set({ resolvedElements: resolved, isResolving: false });
    } catch (error) {
      console.error('[PreviewStore] Resolution failed:', error);
      set({ isResolving: false });
    }
  },

  setPreviewZoom: (zoom: number) => {
    set({ previewZoom: Math.max(0.25, Math.min(3, zoom)) });
  },

  toggleBoundaries: () => {
    set((state) => ({ showBoundaries: !state.showBoundaries }));
  },

  toggleLayerIndicators: () => {
    set((state) => ({ showLayerIndicators: !state.showLayerIndicators }));
  },

  runADAChecks: () => {
    const { canvasState } = useStudioStore.getState();
    const { resolvedElements } = get();

    const results = runADAComplianceChecks(canvasState.elements, resolvedElements);
    set({ adaCheckResults: results });
  },

  toggleADAPanel: () => {
    set((state) => ({ showADAPanel: !state.showADAPanel }));
  },

  setResolvedElements: (elements: ResolvedElement[]) => {
    set({ resolvedElements: elements, isResolving: false });
  },
}));
